import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { AI_DIRECTIVE_BINDINGS_V1, bindingById } from '@pushly/models/lib/types'
import type { PromotionDimension } from './promotion-suggestions'

/**
 * Moves a promoted statement out of its prose tier and into the Guaranteed spec, locally, the
 * moment the author confirms it (NS-2648) — so the rule lands where they just asked it to land
 * instead of sitting in Guidance behind a "click Analyze" nag.
 *
 * THIS IS A PREDICTION, and it is only allowed to be one because the server's projection is
 * deterministic. `resolveBindings` iterates the BINDING CATALOG, not the model's output, so
 * `resolved` — and therefore `spec.and` — is always in catalog order regardless of what the LLM
 * emitted or in what sequence. That is what makes the position of a newly inserted binding
 * predictable rather than a guess; `projectSpec` then maps each entry to
 * `{ [attribute]: { [operator]: value } }`, which is mirrored exactly below.
 *
 * WHAT THIS IS NOT: it is not what gets saved. `compileAndPersist` recompiles the body
 * server-side and refuses to persist anything that disagrees with its own fresh result, so a
 * locally-constructed directive could never be written even if we wanted it to be. Submit stays
 * gated on a real compile of the exact text on screen — the prediction only ever drives what the
 * author is looking at while that compile runs, and is replaced by the real result when it lands.
 *
 * The prose tiers are where a prediction can legitimately differ from the compiler: removing the
 * promoted statement is certain, but a recompile may also re-tier a neighboring sentence. That
 * correction is visible and arrives within seconds, which is the trade for not making the author
 * wait to see their own edit take effect.
 */
export function applyPromotionToCompiled(
    compiled: CompiledDirective,
    itemText: string,
    dimension: PromotionDimension,
    value: number,
    sentenceSpan: { start: number; end: number; quote: string },
): CompiledDirective {
    const serialized = compiled.serialize() as any

    // The promoted statement is gone from the author's text, so it cannot survive in the tier it
    // used to occupy. Removed from all three: the compiler decides which prose tier a statement
    // lands in, and this has to hold wherever it happened to be.
    const withoutItem = (items: any[] | undefined) => (items ?? []).filter((item) => item?.text !== itemText)

    const resolved = insertBinding(serialized.resolved ?? [], dimension, value, sentenceSpan)

    return CompiledDirective.from({
        ...serialized,
        resolved,
        spec: projectSpec(resolved),
        assertions: withoutItem(serialized.assertions),
        style: withoutItem(serialized.style),
        unmapped: withoutItem(serialized.unmapped),
    })
}

/**
 * The binding list with this promotion in it, in catalog order.
 *
 * An existing entry for the same binding is REPLACED rather than appended to. Every promotable
 * binding here is an integer limit, and two `title_max_length` conditions in one spec would mean
 * the tighter one silently wins — which is not what an author who adjusted a limit is asking for.
 */
function insertBinding(
    resolved: any[],
    dimension: PromotionDimension,
    value: number,
    sentenceSpan: { start: number; end: number; quote: string },
): any[] {
    const entry = {
        binding: { id: dimension.bindingId, value },
        // The sentence was just spliced in at a position we computed, so unlike the compiler —
        // which has to locate its own quotes — the attribution here is exact. The new Guaranteed
        // row links back to its source text immediately.
        sources: [sentenceSpan],
    }

    const next = [...resolved.filter((r) => r?.binding?.id !== dimension.bindingId), entry]
    const catalogIndex = (id: string) => {
        const index = AI_DIRECTIVE_BINDINGS_V1.findIndex((b) => b.id === id)
        // An id the catalog no longer carries sorts last rather than first — it is stale data,
        // and it should not reorder the bindings that are still real.
        return index === -1 ? Number.MAX_SAFE_INTEGER : index
    }

    // Stable (ES2019+), which matters: a `string_array` binding fans out to several entries
    // sharing one id, and their relative order is the order its terms were listed.
    return next.sort((a, b) => catalogIndex(a?.binding?.id) - catalogIndex(b?.binding?.id))
}

/**
 * Mirrors sailfish/sealab's `projectSpec` exactly — one condition per resolved binding, in the
 * same order, wrapped in a bare AND group. An empty set is `{}`, not `{ and: [] }`: the PQuery
 * reader rejects empty groups.
 */
function projectSpec(resolved: any[]): Record<string, unknown> {
    const conditions: Record<string, unknown>[] = []

    for (const entry of resolved) {
        const binding = entry?.binding?.id ? bindingById(entry.binding.id) : undefined
        if (!binding) continue
        conditions.push({ [binding.attribute]: { [binding.operator]: entry.binding.value } })
    }

    return conditions.length > 0 ? { and: conditions } : {}
}

/**
 * The limits already present in a compile, keyed by the PQuery attribute they enforce.
 *
 * Used by the confirm step to refuse a promotion aimed at a dimension the author's own text
 * already sets. The compiler never SUGGESTS an enforced dimension, but the confirm step lets the
 * author retarget — and a real directive reached it: "Write short sentences" is offered as a
 * character limit, and switching the measure to Words aims it at `title_max_words`, which their
 * own "Use a maximum of 6 words in the title." already sets.
 *
 * Keyed by attribute rather than binding id because that is what a conflict actually is: two
 * rules constraining `title.wordLength`, however they were phrased.
 */
export function enforcedLimitsByAttribute(compiled: CompiledDirective | null): Record<string, unknown> {
    const enforced: Record<string, unknown> = {}
    if (!compiled) return enforced

    for (const entry of (compiled.resolved ?? []) as any[]) {
        const binding = entry?.binding?.id ? bindingById(entry.binding.id) : undefined
        // Integer limits only. A `string_array` binding fans out to many entries on one
        // attribute (`message.raw` holds every banned term), so "already set" is meaningless
        // there — and nothing promotable targets it anyway.
        if (!binding || binding.value.kind !== 'integer') continue
        enforced[binding.attribute] = entry.binding.value
    }

    return enforced
}
